<script lang="ts">
  import { onMount } from 'svelte';
  import { location, push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import Table from '../../Table.svelte';
  import PieChart from '../../charts/PieChart.svelte';
  import StatusBadge from '../../StatusBadge.svelte';
  import Pagination from '../../Pagination.svelte';
  import Breadcrumbs from '../../Breadcrumbs.svelte';
  import employeeProfileService, { 
  type EmployeeProfile, 
  type DonationRequest,
  type EmployerContribution,
  EmployeeProfileService
} from '../../../lib/api/admin/employees/employee-profile.service';

  import toast from 'svelte-french-toast';
  import BackButton from '../../BackButton.svelte';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import UsersService from '../../../lib/api/admin/users.service';

  // Props
  export let employeeUuid: string = '';

  // Tab state
  type TabType = 'donations' | 'employers' | 'activity';
  let activeTab: TabType = 'donations';

  // State
  let employeeProfile: EmployeeProfile | null = null;
  let employerContribution: EmployerContribution | null = null;
  let donationHistory: DonationRequest[] = [];
  let isLoading = true;
  let error = '';
  
  // Pagination state for donation requests
  let donationRequestsPage = 1;
  let donationRequestsPerPage = 10;
  let donationRequestsTotalPages = 1;
  let donationRequestsTotalItems = 0;
  let isLoadingDonationRequests = false;

  // Employers data (extracted from donation requests)
  interface EmployerData {
    uuid: string;
    name: string;
    totalDonations: number;
    totalHours: number;
    totalValue: number;
    lastDonationDate: string | null;
  }

  let employers: EmployerData[] = [];
  let activityHistory: DonationRequest[] = [];

  // Delete employee modal
  let showDeleteModal = false;
  let deleteLoading = false;

  // Extract UUID from route if not provided
  $: if (!employeeUuid && $location) {
    const pathParts = $location.split('/');
    const uuidIndex = pathParts.findIndex(part => part === 'employee-profile');
    if (uuidIndex !== -1 && pathParts[uuidIndex + 1]) {
      employeeUuid = pathParts[uuidIndex + 1];
    }
  }

  // Load employee profile data
  async function loadEmployeeProfile() {
    if (!employeeUuid) {
      error = 'Employee UUID is required';
      isLoading = false;
      return;
    }

    try {
      isLoading = true;
      error = '';

      // Load employee profile
      const profileResponse = await employeeProfileService.getEmployeeProfile(employeeUuid);
      
      if (profileResponse.data?.success && profileResponse.data.data) {
        employeeProfile = profileResponse.data.data;
      } else {
        error = profileResponse.data?.message || 'Failed to load employee profile';
        toast.error(error);
      }

      // Load employer contribution
      const contributionResponse = await employeeProfileService.getEmployeeEmployerContribution(employeeUuid);
      
      if (contributionResponse.data?.success && contributionResponse.data.data) {
        employerContribution = contributionResponse.data.data;
      } else {
        console.warn('Failed to load employer contribution:', contributionResponse.data?.message);
        employerContribution = null;
      }

      // Load initial donation requests
      await loadDonationRequests();

      // Extract employers from donation history
      extractEmployersFromDonations();

      // Load activity history
      loadActivityHistory();

    } catch (err) {
      console.error('Error loading employee profile:', err);
      error = 'An error occurred while loading employee profile';
      toast.error(error);
    } finally {
      isLoading = false;
    }
  }

  // Extract unique employers from donation requests
  function extractEmployersFromDonations() {
    // Ensure donationHistory is always an array
    if (!Array.isArray(donationHistory)) {
      donationHistory = [];
    }
    
    const list = donationHistory;
    if (list.length === 0) {
      employers = [];
      return;
    }

    const employerMap = new Map<string, EmployerData>();

    list.forEach((donation) => {
      if (donation.employer && donation.employer.uuid) {
        const employerUuid = donation.employer.uuid;
        const employerName = donation.employer.name;

        if (!employerMap.has(employerUuid)) {
          employerMap.set(employerUuid, {
            uuid: employerUuid,
            name: employerName,
            totalDonations: 0,
            totalHours: 0,
            totalValue: 0,
            lastDonationDate: null
          });
        }

        const employer = employerMap.get(employerUuid)!;
        employer.totalDonations += 1;
        employer.totalHours += donation.leave_hours || 0;
        employer.totalValue += donation.leave_value || 0;

        // Update last donation date
        if (donation.created_at) {
          if (!employer.lastDonationDate || new Date(donation.created_at) > new Date(employer.lastDonationDate)) {
            employer.lastDonationDate = donation.created_at;
          }
        }
      }
    });

    employers = Array.from(employerMap.values()).sort((a, b) => {
      const dateA = a.lastDonationDate ? new Date(a.lastDonationDate).getTime() : 0;
      const dateB = b.lastDonationDate ? new Date(b.lastDonationDate).getTime() : 0;
      return dateB - dateA; // Sort by most recent first
    });
  }

  // Load activity history (all donation requests sorted by date)
  function loadActivityHistory() {
    // Ensure donationHistory is always an array
    if (!Array.isArray(donationHistory)) {
      donationHistory = [];
    }
    
    const list = donationHistory;
    if (list.length === 0) {
      activityHistory = [];
      return;
    }

    activityHistory = [...list].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateB - dateA; // Most recent first
    });
  }

  // Load paginated donation requests
  async function loadDonationRequests() {
    if (!employeeUuid) return;

    try {
      isLoadingDonationRequests = true;
      
      const response = await employeeProfileService.getEmployeeDonationRequests(employeeUuid, {
        page: donationRequestsPage,
        per_page: donationRequestsPerPage,
        sort_by: 'created_at',
        sort_direction: 'desc'
      });
      
      if (response.data?.success && response.data.data) {
        const data = response.data.data;
        
        // Ensure we always get an array
        let items: DonationRequest[] = [];
        if (Array.isArray(data)) {
          items = data;
        } else if (data && typeof data === 'object' && 'items' in data && Array.isArray((data as { items?: DonationRequest[] }).items)) {
          items = (data as { items: DonationRequest[] }).items;
        }
        
        donationHistory = items;
        
        // Extract paging information
        let paging: { last_page?: number; total?: number } | undefined;
        if (data && typeof data === 'object' && !Array.isArray(data) && 'paging' in data) {
          paging = (data as { paging?: { last_page?: number; total?: number } }).paging;
        } else {
          paging = (response as any).paging;
        }
        
        donationRequestsTotalPages = paging?.last_page ?? 1;
        donationRequestsTotalItems = paging?.total ?? 0;
        
        // Update employers and activity when donation requests are loaded
        extractEmployersFromDonations();
        loadActivityHistory();
      } else {
        console.warn('Failed to load donation requests:', response.data?.message);
        donationHistory = [];
        employers = [];
        activityHistory = [];
      }
    } catch (err) {
      console.error('Error loading donation requests:', err);
      donationHistory = [];
      employers = [];
      activityHistory = [];
    } finally {
      isLoadingDonationRequests = false;
    }
  }

  // Handle pagination for donation requests
  function handleDonationRequestsPageChange(event: CustomEvent<{ page: number }>) {
    donationRequestsPage = event.detail.page;
    loadDonationRequests();
  }

  // Handle view donation
  function handleViewDonation(donationUuid: string) {
    push(`/view-donation/${donationUuid}`);
  }

  // Handle tab change
  function handleTabChange(tab: TabType) {
    activeTab = tab;
  }

  // Handle back navigation
  function handleBack() {
    push('/manage-employees');
  }

  function openDeleteModal() {
    showDeleteModal = true;
  }

  function closeDeleteModal() {
    if (!deleteLoading) showDeleteModal = false;
  }

  async function handleConfirmDelete() {
    if (!employeeUuid) return;
    try {
      deleteLoading = true;
      await UsersService.deleteUser(employeeUuid);
      toast.success('Employee deleted');
      showDeleteModal = false;
      push('/manage-employees');
    } catch (err) {
      toast.error('Failed to delete employee');
      console.error('Delete employee error:', err);
    } finally {
      deleteLoading = false;
    }
  }

  // Breadcrumbs items
  $: breadcrumbsItems = [
    { label: 'Admin', href: '/admin-dashboard' },
    { label: 'Employee Management', href: '/manage-employees' },
    { label: employeeProfile?.full_name || 'Employee Profile' }
  ];

  // Table columns for employers
  const employersColumns = [
    {
      key: 'name',
      label: 'Employer Name',
      width: '30%'
    },
    {
      key: 'totalDonations',
      label: 'Total Donations',
      width: '15%',
      render: (value: number) => value.toString()
    },
    {
      key: 'totalHours',
      label: 'Total Hours',
      width: '15%',
      render: (value: number) => `${value || 0} hrs`
    },
    {
      key: 'totalValue',
      label: 'Total Value',
      width: '20%',
      render: (value: number) => `$${(value || 0).toLocaleString()}`
    },
    {
      key: 'lastDonationDate',
      label: 'Last Donation',
      width: '20%',
      render: (value: string | null) => {
        if (!value) return 'N/A';
        return EmployeeProfileService.formatDate(value);
      }
    }
  ];

  // Table columns for activity history
  const activityColumns = [
    {
      key: 'created_at',
      label: 'Date',
      width: '15%',
      render: (value: string) => {
        if (!value) return 'N/A';
        return EmployeeProfileService.formatDate(value);
      }
    },
    {
      key: 'type',
      label: 'Activity Type',
      width: '20%',
      render: (value: any, row: any) => {
        return 'Donation Request';
      }
    },
    {
      key: 'charity.name',
      label: 'Charity',
      width: '25%',
      render: (value: any, row: any) => row.charity?.name || 'N/A'
    },
    {
      key: 'leave_hours',
      label: 'Hours',
      width: '10%',
      render: (value: number) => `${value || 0} hrs`
    },
    {
      key: 'donation_request_status.name',
      label: 'Status',
      width: '24%',
      align: 'center',
      render: (value: any, row: any) => {
        const statusName = row.donation_request_status?.name || 'Unknown';
        const statusClasses: Record<string, string> = {
          'Processed': 'completed',
          'Approved': 'approved',
          'Pending': 'pending',
          'Rejected': 'rejected',
          'Completed': 'completed'
        };
        
        const statusClass = statusClasses[statusName] || 'pending';
        
        let icon = '';
        if (statusName === 'Processed' || statusName === 'Completed' || statusName === 'Approved') {
          icon = `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>`;
        } else if (statusName === 'Rejected') {
          icon = `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                  </svg>`;
        } else if (statusName === 'Pending') {
          icon = `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                  </svg>`;
        } else {
          icon = `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                  </svg>`;
        }
        
        return `<div class="status-badge ${statusClass}"><span class="icon" aria-hidden="true">${icon}</span><span class="text">${statusName}</span></div>`;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '10%',
      render: (value: any, row: any) => {
        return `<button class="view-btn" onclick="handleViewDonation('${row.uuid}')">View</button>`;
      }
    }
  ];

  // Table columns for donation history
  const donationHistoryColumns = [
    {
      key: 'created_at',
      label: 'Date',
      width: '12%',
      render: (value: string) => {
        if (!value) return 'N/A';
        try {
          const date = new Date(value);
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          const day = date.getDate();
          const year = date.getFullYear();
          return `${month} ${day}, ${year}`;
        } catch {
          return 'Invalid Date';
        }
      }
    },
    {
      key: 'charity.name',
      label: 'Charity Name',
      width: '25%',
      render: (value: any, row: any) => row.charity?.name || 'N/A'
    },
    {
      key: 'leave_hours',
      label: 'Hours',
      width: '10%',
      render: (value: number) => `${value || 0} hrs`
    },
    {
      key: 'leave_value',
      label: 'Employee Value',
      width: '15%',
      render: (value: number) => `$${(value || 0).toLocaleString()}`
    },
    {
      key: 'employer_top_up',
      label: 'Employer Contribution',
      width: '15%',
      render: (value: number) => `$${(value || 0).toLocaleString()}`
    },
    {
      key: 'donation_request_status.name',
      label: 'Status',
      width: '16%',
      align: 'center',
      render: (value: any, row: any) => {
        const statusName = row.donation_request_status?.name || 'Unknown';
        const statusClasses: Record<string, string> = {
          'Processed': 'completed',
          'Approved': 'approved',
          'Pending': 'pending',
          'Rejected': 'rejected',
          'Completed': 'completed'
        };
        
        const statusClass = statusClasses[statusName] || 'pending';
        
        // SVG icons matching StatusBadge component
        let icon = '';
        if (statusName === 'Processed' || statusName === 'Completed' || statusName === 'Approved') {
          // Tick icon for all approved/processed statuses
          icon = `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>`;
        } else if (statusName === 'Rejected') {
          icon = `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                  </svg>`;
        } else if (statusName === 'Pending') {
          icon = `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                  </svg>`;
        } else {
          icon = `<svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                  </svg>`;
        }
        
        return `<div class="status-badge ${statusClass}"><span class="icon" aria-hidden="true">${icon}</span><span class="text">${statusName}</span></div>`;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '10%',
      render: (value: any, row: any) => {
        return `<button class="view-btn" onclick="handleViewDonation('${row.uuid}')">View</button>`;
      }
    }
  ];

  // Pie chart data for employer participation
  $: {
    if (employerContribution) {
      // Data is available for pie chart
    }
  }
  
  $: pieChartData = employerContribution?.employerContributions ? {
    approved: Number(employerContribution.employerContributions.approved) || 0,
    pending: Number(employerContribution.employerContributions.pending) || 0,
    rejected: Number(employerContribution.employerContributions.rejected) || 0
  } : { approved: 0, pending: 0, rejected: 0 };

  // Make functions globally available for onclick handlers
  onMount(() => {
    if (typeof window !== 'undefined') {
      (window as any).handleViewDonation = handleViewDonation;
    }
    loadEmployeeProfile();
  });
</script>

<div class="employee-profile">
  <Breadcrumbs items={breadcrumbsItems} />
  <BackButton text="Back to Employee Management" onClick={handleBack} />
  {#if isLoading}
    <div style="min-height: 400px; padding: 2rem;">
      <LoadingSkeleton lines={8} height="2rem" />
    </div>
  {:else if error}
    <ErrorState 
      message="Failed to load employee profile" 
      description={error}
      onRetry={loadEmployeeProfile}
    />
  {:else if employeeProfile}
    <!-- Employee Header Section -->
    <div class="profile-header">
      <div class="header-info">
            <h1 class="employee-name">{employeeProfile.full_name}</h1>
        <div class="employee-meta">
          <span class="meta-item">{employeeProfile.email}</span>
          <span class="meta-separator">•</span>
          <span class="meta-item">{employeeProfile.phone || 'No phone'}</span>
        </div>
      </div>
      <div class="header-stats">
        <div class="stat-item">
          <span class="stat-label">Total Hours</span>
          <span class="stat-value">{employeeProfile.total_donated_hours || 0}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Total Value</span>
          <span class="stat-value">${(employeeProfile.total_donation_amount || 0).toLocaleString()}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Total Donations</span>
          <span class="stat-value">{donationRequestsTotalItems || 0}</span>
        </div>
      </div>
          </div>
          
    <!-- Profile Details Card -->
    <div class="profile-details-card">
      <div class="card-header">
        <h2>Profile Details</h2>
        <Button variant="danger" on:click={openDeleteModal}>
          Delete Employee
        </Button>
      </div>
      
      <div class="card-content">
        <div class="details-grid">
            <div class="detail-item">
              <span class="detail-label">Email</span>
              <span class="detail-value">{employeeProfile.email}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Phone</span>
            <span class="detail-value">{employeeProfile.phone || 'N/A'}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Join Date</span>
              <span class="detail-value">
                {employeeProfile.email_verified_at ? EmployeeProfileService.formatDate(employeeProfile.email_verified_at) : 'Not verified'}
              </span>
            </div>
            <div class="detail-item">
            <span class="detail-label">Status</span>
            <span class="detail-value">
              <StatusBadge status={employeeProfile.user_status?.name || 'Unknown'} />
            </span>
            </div>
            <div class="detail-item">
            <span class="detail-label">Pending Hours</span>
                <span class="detail-value">{employeeProfile.total_pending_hours || 0} Hrs</span>
              </div>
              <div class="detail-item">
            <span class="detail-label">Last Donation</span>
                <span class="detail-value">
                  {employeeProfile.last_donation_request_date ? EmployeeProfileService.formatDate(employeeProfile.last_donation_request_date) : 'No donations yet'}
                </span>
              </div>
          {#if employeeProfile.job_title}
            <div class="detail-item">
              <span class="detail-label">Job Title</span>
              <span class="detail-value">{employeeProfile.job_title}</span>
          </div>
          {/if}
          {#if employeeProfile.location}
            <div class="detail-item">
              <span class="detail-label">Location</span>
              <span class="detail-value">{employeeProfile.location}</span>
            </div>
          {/if}
          </div>
        </div>
      </div>

    <!-- Tabs Section -->
    <div class="tabs-container">
      <div class="tabs">
        <button 
          class="tab-button" 
          class:active={activeTab === 'donations'} 
          on:click={() => handleTabChange('donations')}
        >
          Donations ({donationRequestsTotalItems || 0})
        </button>
        <button 
          class="tab-button" 
          class:active={activeTab === 'employers'} 
          on:click={() => handleTabChange('employers')}
        >
          Employers ({employers.length})
        </button>
        <button 
          class="tab-button" 
          class:active={activeTab === 'activity'} 
          on:click={() => handleTabChange('activity')}
        >
          Activity ({activityHistory.length})
        </button>
        </div>
      </div>

    <!-- Tab Content -->
    <div class="tab-content">
      {#if activeTab === 'donations'}
        <div class="content-card">
        <div class="card-header">
          <h2>Donation History</h2>
        </div>
        
          <div class="card-content">
          {#if isLoadingDonationRequests}
            <div class="loading-message">Loading donation requests...</div>
          {:else if donationHistory.length > 0}
            <Table 
              columns={donationHistoryColumns}
              data={donationHistory}
                onRowClick={(row) => handleViewDonation(row.uuid)}
            />
            
            {#if donationRequestsTotalPages > 1}
              <div class="pagination-wrapper">
                <Pagination
                  currentPage={donationRequestsPage}
                  totalPages={donationRequestsTotalPages}
                  on:pageChange={handleDonationRequestsPageChange}
                />
              </div>
            {/if}
          {:else}
              <div class="empty-state">
                <p>No donation history available</p>
              </div>
          {/if}
        </div>
      </div>
      {:else if activeTab === 'employers'}
        <div class="content-card">
          <div class="card-header">
            <h2>Employers</h2>
          </div>
          
          <div class="card-content">
            {#if employers.length > 0}
              <Table 
                columns={employersColumns}
                data={employers}
              />
            {:else}
              <div class="empty-state">
                <p>No employers found</p>
              </div>
            {/if}
          </div>
        </div>
      {:else if activeTab === 'activity'}
        <div class="content-card">
          <div class="card-header">
            <h2>Activity History</h2>
          </div>
          
          <div class="card-content">
            {#if activityHistory.length > 0}
              <Table 
                columns={activityColumns}
                data={activityHistory}
                onRowClick={(row) => handleViewDonation(row.uuid)}
              />
            {:else}
              <div class="empty-state">
                <p>No activity history available</p>
              </div>
            {/if}
          </div>
        </div>
      {/if}
    </div>
  {:else}
    <div class="empty-state">
      <p>Employee not found</p>
    </div>
  {/if}
</div>

<!-- Delete employee confirmation modal -->
{#if showDeleteModal}
  <div class="modal-overlay" role="presentation" on:click|self={closeDeleteModal} on:keydown={(e) => e.key === 'Escape' && closeDeleteModal()}>
    <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="delete-employee-title">
      <h3 id="delete-employee-title">Delete Employee</h3>
      <p>Are you sure you want to delete <strong>{employeeProfile?.full_name}</strong>? This action cannot be undone and will remove all associated data.</p>
      <div class="modal-actions">
        <Button variant="secondary" on:click={closeDeleteModal} disabled={deleteLoading}>Cancel</Button>
        <Button variant="danger" on:click={handleConfirmDelete} disabled={deleteLoading}>
          {deleteLoading ? 'Deleting...' : 'Delete'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .employee-profile {
    padding: 1.5rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  /* Profile Header */
  .profile-header {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 2rem;
  }

  .header-info {
    flex: 1;
  }

  .employee-name {
    font-size: 1.875rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  .employee-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .meta-separator {
    color: #d1d5db;
  }

  .header-stats {
    display: flex;
    gap: 2rem;
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .stat-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
  }

  /* Tabs */
  .tabs-container {
    background: white;
    border-radius: 12px;
    padding: 0 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .tabs {
    display: flex;
    gap: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .tab-button {
    padding: 1rem 2rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: -1px;
  }

  .tab-button:hover {
    color: #111827;
  }

  .tab-button.active {
    color: #111827;
    border-bottom-color: var(--color-primary);
  }

  /* Tab Content */
  .tab-content {
    margin-bottom: 1.5rem;
  }

  .content-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .card-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .card-header h2 {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .card-content {
    padding: 1rem 1.5rem;
  }

  /* Profile Details Card */
  .profile-details-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-bottom: 1.5rem;
  }

  .details-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
  }

  .detail-item {
    background-color: #f9fafb;
    border-radius: 6px;
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .detail-label {
    font-weight: 500;
    font-size: 0.75rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  .detail-value {
    font-weight: 500;
    font-size: 0.875rem;
    color: #111827;
  }

  .empty-state {
    text-align: center;
    color: #6b7280;
    padding: 2rem;
  }

  .empty-state p {
    margin: 0;
  }

  .loading-message {
    text-align: center;
    color: #6b7280;
    padding: 2rem;
  }

  .pagination-wrapper {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: center;
  }

  /* Status badges - matching StatusBadge component */
  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.5;
    gap: 0.375rem;
  }

  :global(.status-badge .icon) {
    display: flex;
    align-items: center;
  }

  :global(.status-badge .text) {
    margin-top: 1px;
  }

  :global(.status-badge.completed) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.status-badge.approved) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  :global(.status-badge.pending) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.status-badge.rejected) {
    background-color: #fee2e2;
    color: #991b1b;
  }

  /* View button */
  :global(.view-btn) {
    background: #111827;
    color: white;
    border: none;
    border-radius: 50px;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  :global(.view-btn:hover) {
    background: #1f2937;
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .profile-header {
      flex-direction: column;
      gap: 1.5rem;
    }

    .header-stats {
      width: 100%;
      justify-content: space-around;
    }
    
    .details-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
    }
  }

  @media (max-width: 768px) {
    .employee-profile {
      padding: 1rem;
    }
    
    .profile-header {
      padding: 1.5rem;
    }

    .employee-name {
      font-size: 1.5rem;
    }

    .header-stats {
      flex-direction: column;
      gap: 1rem;
      width: 100%;
    }

    .stat-item {
      flex-direction: row;
      justify-content: space-between;
      padding: 0.75rem;
      background: #f9fafb;
      border-radius: 8px;
    }

    .tabs {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .tab-button {
      padding: 0.75rem 1rem;
      font-size: 0.8125rem;
      white-space: nowrap;
    }

    .details-grid {
      grid-template-columns: 1fr;
      gap: 0.5rem;
    }

    .detail-item {
      padding: 0.5rem;
    }

    .card-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
      padding: 1rem;
    }

    .card-content {
      padding: 1rem;
    }
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    max-width: 450px;
    width: 90%;
    padding: 2rem;
  }

  .modal-content h3 {
    margin: 0 0 1rem 0;
    font-size: 1.25rem;
    color: #991b1b;
  }

  .modal-content p {
    color: #374151;
    margin: 0 0 1.5rem 0;
    line-height: 1.6;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }
</style> 